import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Bell, ChevronDown } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type SellerNotificationSettingsFragment,
  Seller_Notification_Frequency_Enum_Enum,
  type UpdateListingMutation,
} from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UPDATE_LISTING } from '@modules/listings/lotsQueries';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

export const SELLER_NOTIFICATION_SETTINGS_FRAGMENT = gql(/* GraphQL */ `
  fragment SellerNotificationSettings on lots {
    id
    seller_notification_frequency
  }
`);

const NOTIFICATION_FREQUENCIES: Seller_Notification_Frequency_Enum_Enum[] = [
  Seller_Notification_Frequency_Enum_Enum.Never,
  Seller_Notification_Frequency_Enum_Enum.Daily,
  Seller_Notification_Frequency_Enum_Enum.Weekly,
  Seller_Notification_Frequency_Enum_Enum.Monthly,
];

export const translateNotificationFrequency = (
  frequency: Seller_Notification_Frequency_Enum_Enum | null | undefined,
  t: Translate,
) => {
  switch (frequency) {
    case Seller_Notification_Frequency_Enum_Enum.Never:
      return t('Never');
    case Seller_Notification_Frequency_Enum_Enum.Daily:
      return t('Daily');
    case Seller_Notification_Frequency_Enum_Enum.Weekly:
      return t('Weekly');
    case Seller_Notification_Frequency_Enum_Enum.Monthly:
      return t('Monthly');
    default:
      return t('Never');
  }
};

interface SellerNotificationSettingsProps {
  listing?: SellerNotificationSettingsFragment | null;
  isLoading?: boolean;
  readOnly?: boolean;
}

const NotificationLabel: React.FC = () => {
  const { t } = useLocale();
  return (
    <>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          color: customPalette.gray600,
        }}
      >
        <Bell size={16} color="currentColor" />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('Notify seller of new buyer leads')}:
      </Typography>
    </>
  );
};

export const SellerNotificationSettingsSkeleton: React.FC = () => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: 'center',
    }}
  >
    <NotificationLabel />
    <Skeleton width={80} height={32} variant="rounded" />
  </Stack>
);

export const SellerNotificationSettings: React.FC<
  SellerNotificationSettingsProps
> = ({ listing, isLoading = false, readOnly = false }) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [updateLot, { loading }] = useMutation<UpdateListingMutation>(
    UPDATE_LISTING,
    { onError: setErrorLike },
  );

  const currentFrequency =
    listing?.seller_notification_frequency ??
    Seller_Notification_Frequency_Enum_Enum.Never;

  const onFrequencyChange = (
    frequency: Seller_Notification_Frequency_Enum_Enum,
  ) => {
    if (listing == null || frequency === currentFrequency) {
      return;
    }

    updateLot({
      variables: {
        id: listing.id,
        lot: { seller_notification_frequency: frequency },
      },
    });
  };

  if (isLoading) {
    return <SellerNotificationSettingsSkeleton />;
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <NotificationLabel />
        <Select
          size="small"
          variant="standard"
          value={currentFrequency}
          onChange={event =>
            onFrequencyChange(
              event.target.value as Seller_Notification_Frequency_Enum_Enum,
            )
          }
          disabled={loading || listing == null || readOnly}
          disableUnderline
          IconComponent={props =>
            loading ? (
              <CircularProgress
                disableShrink
                size={14}
                sx={{ color: 'text.secondary' }}
                {...props}
              />
            ) : (
              <ChevronDown size={16} {...props} />
            )
          }
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
            '& .MuiSelect-select': {
              py: 0.25,
              pl: 0,
              pr: 2.5,
              minHeight: 'auto',
            },
            '& .MuiSelect-icon': {
              right: 0,
              top: 'calc(50% - 7px)',
            },
          }}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            slotProps: {
              paper: {
                sx: { minWidth: 'auto' },
              },
              list: {
                dense: true,
              },
            },
          }}
        >
          {NOTIFICATION_FREQUENCIES.map(frequency => (
            <MenuItem key={frequency} value={frequency}>
              {translateNotificationFrequency(frequency, t)}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
